import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Copy, Sparkles } from "lucide-react";
import api from "../lib/api";
import { PageBackdrop } from "../components/PageBackdrop";

export default function PromotionsPage() {
    const [promotions, setPromotions] = useState([]);
    const navigate = useNavigate();

    useEffect(() => { api.get("/promotions").then((r) => setPromotions(r.data)); }, []);

    const copyCode = (e, code) => {
        e.preventDefault();
        e.stopPropagation();
        if (!code) return;
        navigator.clipboard?.writeText(code);
        toast.success(`Code ${code} copied`);
    };

    return (
        <div className="relative isolate min-h-screen pt-32 pb-32 grain" data-testid="promotions-page">
            <PageBackdrop testid="promotions-bg" />
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <div className="text-overline">Now On</div>
                <h1 className="font-serif-display text-5xl sm:text-7xl text-[#3D0C1F] mt-3">Promotions.</h1>
                <p className="text-[#7A5262] mt-3 max-w-xl">Curated offers that make luxury feel just a touch more accessible.</p>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-12">
                    {promotions.map((p) => {
                        const expired = !!p.expires_at && new Date(p.expires_at).getTime() <= Date.now();
                        const soldOut = typeof p.seats_left === "number" && p.seats_left <= 0;
                        const unavailable = expired || soldOut;
                        const goToBook = () => {
                            if (unavailable) return;
                            if (p.type === "package") navigate(`/book?promo=${p.id}`);
                            else if (p.coupon_code) navigate(`/book?coupon=${encodeURIComponent(p.coupon_code)}&from=${p.id}`);
                            else navigate(`/book?from=${p.id}`);
                        };
                        return (
                            <div
                                key={p.id}
                                role={unavailable ? undefined : "button"}
                                tabIndex={unavailable ? -1 : 0}
                                onClick={unavailable ? undefined : goToBook}
                                onKeyDown={unavailable ? undefined : (e) => { if (e.key === "Enter") goToBook(); }}
                                className={`card-luxe grain group flex flex-col ${unavailable ? "" : "cursor-pointer"}`}
                                data-testid={`promo-${p.id}`}
                            >
                                {p.image && (
                                    <div className="aspect-[16/9] overflow-hidden relative">
                                        <img src={p.image} alt={p.title} loading="lazy" decoding="async" className={`w-full h-full object-cover transition-all duration-700 ${unavailable ? "grayscale opacity-60" : "group-hover:scale-105"}`} />
                                        <div className="absolute top-2 right-2 bg-[#6B1535] text-[#FFF8F2] px-2 py-0.5 text-[0.55rem] tracking-[0.2em] uppercase font-semibold">{p.discount_label}</div>
                                        {unavailable && (
                                            <div className="absolute top-2 left-2 bg-[#FBF6F0]/85 border border-[#6B1535]/15 text-[#3D0C1F] px-2 py-0.5 text-[0.55rem] tracking-[0.2em] uppercase">
                                                {soldOut ? "Sold out" : "Ended"}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="p-3 flex-1 flex flex-col">
                                    <h3 className="font-serif-display text-base leading-tight text-[#3D0C1F]">{p.title}</h3>
                                    <p className="text-[0.7rem] text-[#8A6574] mt-1 line-clamp-2 leading-snug">{p.description}</p>

                                    {p.coupon_code && !unavailable && p.type !== "package" && (
                                        <button
                                            type="button"
                                            onClick={(e) => copyCode(e, p.coupon_code)}
                                            className="mt-2 inline-flex items-center gap-1.5 self-start px-2 py-1 border border-[#C29A5B]/40 bg-[#6B1535]/5 text-[0.6rem] tracking-[0.16em] uppercase hover:bg-[#6B1535]/10 hover:border-[#C29A5B]/70 transition-colors"
                                            data-testid={`promo-page-code-${p.id}`}
                                            title="Click to copy"
                                        >
                                            <span className="text-[#7A5262]">Code</span>
                                            <span className="gold-text font-medium">{p.coupon_code}</span>
                                            <Copy size={9} className="text-[#7A5262]" />
                                        </button>
                                    )}
                                    {p.type === "package" && p.package_price != null && !unavailable && (
                                        <div className="mt-2 inline-flex items-center gap-1.5 self-start px-2 py-1 border border-[#C29A5B]/40 bg-[#6B1535]/5 text-[0.6rem] tracking-[0.16em] uppercase">
                                            <span className="text-[#7A5262]">Package</span>
                                            <span className="gold-text font-medium">S${p.package_price}</span>
                                        </div>
                                    )}
                                    {!unavailable && (
                                        <div className="mt-2 pt-2 border-t border-[#6B1535]/10 flex items-center justify-between">
                                            <span className="inline-flex items-center gap-1.5 text-[0.65rem] tracking-[0.18em] uppercase text-[#3D0C1F] group-hover:text-[#8F622C] transition-colors">
                                                <Sparkles size={10} className="text-[#8F622C]" /> {p.type === "package" ? "Book package" : "Redeem now"} <ArrowUpRight size={10} />
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
